// Dashboard: the job board. The welcome variant (after the setup wizard) ends by
// walking the owner into the Owner Console; the normal one ends at Settings.
const dashboard = {
  title: 'Dashboard',
  steps: ({ access, companyName, variant, user }) => {
    const owner = access.ownerConsole;
    const provider = access.isOrg2;
    const driver = access.driverOnly || user?.primaryRole === 'driver';
    const steps = [
      {
        target: null,
        title: variant === 'welcome' ? `Welcome to ${companyName}` : 'Your job board',
        body: variant === 'welcome'
          ? `Setup is done and your company is live. This short tour shows where everything is. Each page has its own tour the first time you open it, and you can replay any of them from the ? button at the top.`
          : driver
            ? 'Every job offered to you or assigned to you shows up here, live. This tour shows you around; it takes about a minute.'
            : 'Every customer request lands here and moves across the tabs as it is dispatched, worked and completed. This tour shows you around; it takes about a minute.'
      },
      {
        target: 'dashboard-tabs',
        title: 'Four tabs, one job at a time',
        body: driver
          ? 'Scheduled: jobs booked for later. Active: what you are on now, plus offers waiting for your answer. Completed and Canceled keep your history.'
          : 'Scheduled: booked for a later time. Active: new requests waiting for a provider, and jobs in progress. Completed and Canceled keep the history. A job moves on its own as its status changes.',
        placement: 'bottom'
      },
      {
        target: 'dashboard-tab-active',
        title: 'Try it: open Active Jobs',
        body: 'Click the Active Jobs tab. Pending requests sit on top; jobs already accepted and under way are below them.',
        action: 'click'
      },
      {
        target: 'dashboard-jobs',
        title: 'The job list',
        body: provider || driver
          ? 'Each row is a job. Click a row to open its details: customer, vehicle, location and notes. Offers show Accept and Reject; accepted jobs show the status buttons (En Route, On Scene, Complete) and the ⋮ menu for GOA or unsuccessful requests.'
          : 'Each row is a job. Click a row for its details and history. Buttons on the row dispatch it (nearest driver, pick a driver, or blast to every driver in range), change its status, send a card payment to the provider, or cancel. The ⋮ menu holds the less common actions.',
        placement: 'top'
      },
      {
        target: 'dashboard-add-job',
        title: 'Create a job by hand',
        body: 'Requests from the website and the app arrive on their own. For a phone call, press + and type the job in: customer, vehicle, pickup and drop-off, service and price.',
        placement: 'left',
        when: (c) => !c.access.isOrg2 || c.access.orgAdmin !== 'none'
      },
      {
        target: 'dashboard-filter-toggle',
        title: 'Find a job fast',
        body: 'Click Show Filters to search by PO number, customer, phone, city, service, date or provider. Search applies to every tab.',
        action: 'click',
        when: () => !driver
      },
      {
        target: 'dashboard-filters',
        title: 'Filters',
        body: 'Type what you know and press Search. Clear resets every field. The bell at the top also opens the dashboard already filtered to the job it mentions.',
        placement: 'bottom',
        when: () => !driver
      },
      {
        target: 'bell',
        title: 'Notifications',
        body: 'Every alert sent to your phone also lands here: new requests, acceptances, arrivals, customer replies. Click one to jump to that job.',
        placement: 'bottom'
      },
      {
        target: 'avatar',
        title: 'Your profile',
        body: provider
          ? 'Edit Profile holds your name, phone, password and which alerts you get. Update Tax Info, Upload COI and Upload Background Checks keep your company current so you stay eligible for work.'
          : 'Edit Profile holds your name, phone, password and which alerts you get on each channel. Log Out is here too.',
        placement: 'bottom'
      },
      {
        target: 'help-button',
        title: 'Help is one click away',
        body: 'The ? button replays the tour of whatever page you are on, opens the written help with step-by-step guides, or turns tours off and on.',
        placement: 'bottom'
      },
      {
        target: 'menu-button',
        title: 'The main menu',
        body: 'Every page of the portal is behind this button. Click it now.',
        action: 'click'
      }
    ];

    if (owner && variant === 'welcome') {
      steps.push({
        target: 'nav-owner',
        title: 'The Owner Console',
        body: 'Everything about your company lives here: branding, services and prices, memberships, notifications and hiring (employees and contractors). Provider companies that apply to take your jobs are reviewed under Submissions instead. Click Owner Console to continue the tour there.',
        action: 'click',
        then: 'owner-console',
        placement: 'right'
      });
    } else {
      steps.push({
        target: 'nav-settings',
        title: 'Settings',
        body: provider
          ? 'Your drivers, trucks, users and the rate agreement are under Settings. Click it to continue the tour there.'
          : 'Users, drivers, fleet, sites, rates and dispatch timing are under Settings. Click it to continue the tour there.',
        action: 'click',
        then: 'settings',
        placement: 'right'
      });
    }
    return steps;
  }
};

export default dashboard;
